
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from './components/AdminHeader';
import GamesList from './components/GamesList';
import TeamsManager from './components/TeamsManager';
import NewsManager from './components/NewsManager';
import ResultsManager from './components/ResultsManager';
import SiteSettingsManager from './components/SiteSettingsManager';
import AddGameModal from './components/AddGameModal';

type AdminSection = 'menu' | 'games' | 'results' | 'teams' | 'news' | 'settings';

export default function AdminPage() {
  const [activeSection, setActiveSection] = useState<AdminSection>('menu');
  const [isAddGameModalOpen, setIsAddGameModalOpen] = useState(false);
  const [refreshGames, setRefreshGames] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar autenticación
    const isAdmin = localStorage.getItem('isAdmin');
    if (!isAdmin) {
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('loginTime');
    navigate('/login');
  };

  const handleGameAdded = () => {
    setRefreshGames(prev => prev + 1);
  };

  const menuItems = [
    {
      id: 'games' as AdminSection,
      title: 'Gestión de Partidos',
      description: 'Crear, editar y eliminar partidos',
      icon: 'ri-basketball-line',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'results' as AdminSection,
      title: 'Resultados',
      description: 'Gestionar resultados de partidos',
      icon: 'ri-trophy-line',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'teams' as AdminSection,
      title: 'Equipos',
      description: 'Gestionar equipos, jugadores y staff',
      icon: 'ri-team-line',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'news' as AdminSection,
      title: 'Noticias',
      description: 'Crear y gestionar noticias del club',
      icon: 'ri-newspaper-line',
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'settings' as AdminSection,
      title: 'Configuración',
      description: 'Gestionar logo y configuración del sitio',
      icon: 'ri-settings-3-line',
      color: 'from-pink-500 to-pink-600'
    }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'games':
        return (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-white text-2xl font-bold">Gestión de Partidos</h2>
                <p className="text-gray-400 text-sm">Administra los partidos del club</p>
              </div>
              <button
                onClick={() => setIsAddGameModalOpen(true)}
                className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <i className="ri-add-line text-lg"></i>
                Añadir Partido
              </button>
            </div>
            <GamesList key={refreshGames} />
          </div>
        );
      
      case 'results':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-white text-2xl font-bold">Gestión de Resultados</h2>
              <p className="text-gray-400 text-sm">Administra los resultados de los partidos</p>
            </div>
            <ResultsManager />
          </div>
        );
      
      case 'teams':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-white text-2xl font-bold">Gestión de Equipos</h2>
              <p className="text-gray-400 text-sm">Administra equipos, jugadores y staff</p>
            </div>
            <TeamsManager />
          </div>
        );
      
      case 'news':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-white text-2xl font-bold">Gestión de Noticias</h2>
              <p className="text-gray-400 text-sm">Administra las noticias del club</p>
            </div>
            <NewsManager />
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-white text-2xl font-bold">Configuración del Sitio</h2>
              <p className="text-gray-400 text-sm">Gestiona el logo y configuración general</p>
            </div>
            <SiteSettingsManager />
          </div>
        );
      
      default:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-white text-3xl font-bold mb-2">Panel de Administración</h2>
              <p className="text-gray-400">Selecciona una sección para gestionar</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-5 gap-4 lg:gap-6">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className="group bg-gray-800 hover:bg-gray-700 rounded-xl p-6 transition-all duration-300 hover:scale-105 hover:shadow-xl border border-gray-700 hover:border-gray-600"
                >
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r ${item.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${item.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-white text-lg font-semibold mb-2 group-hover:text-pink-400 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <AdminHeader onLogout={handleLogout} />
      
      <div className="pt-20 px-4 pb-6">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb / Navigation */}
          {activeSection !== 'menu' && (
            <div className="mb-6">
              <button
                onClick={() => setActiveSection('menu')}
                className="flex items-center text-gray-400 hover:text-white transition-colors group"
              >
                <i className="ri-arrow-left-line mr-2 group-hover:-translate-x-1 transition-transform"></i>
                <span className="text-sm">Volver al menú principal</span>
              </button>
            </div>
          )}
          
          {/* Content */}
          <div className="bg-gray-800 rounded-xl p-4 sm:p-6 lg:p-8 shadow-xl border border-gray-700">
            {renderContent()}
          </div>
        </div>
      </div>

      <AddGameModal
        isOpen={isAddGameModalOpen}
        onClose={() => setIsAddGameModalOpen(false)}
        onGameAdded={handleGameAdded}
      />
    </div>
  );
}
